import React, { useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BTN, BTN_PRIMARY } from "@/components/postbacks/shared";
import { EventBoxes } from "@/components/postbacks/PostbackDialog";

const INPUT = "border border-black px-3 py-2 w-full text-sm";
const HELP = {
  meta: "Events Manager → your pixel → Settings → Conversions API → Generate access token.",
  tiktok: "TikTok Events Manager → your pixel → Settings → Generate access token.",
  ga4: "GA4 Admin → Data streams → your stream → Measurement Protocol API secrets → Create.",
};

function Labeled({ id, label, children }) {
  return (
    <>
      <label className="label-mono block mt-4 mb-1" htmlFor={id}>{label}</label>
      {children}
    </>
  );
}

function EventNames({ events, platform, value, onChange }) {
  if (!events.length) return null;
  return (
    <div className="grid sm:grid-cols-2 gap-2 mt-2" data-testid="connection-event-names">
      {events.map((k) => (
        <label key={k} className="text-[11px]">
          <span className="text-zinc-600">{k} sends as</span>
          <input value={value[k] || ""} onChange={(e) => onChange({ ...value, [k]: e.target.value })} maxLength={40}
            placeholder={platform.events[k]} data-testid={`connection-event-name-${k}`} className={`${INPUT} text-xs font-mono`} />
        </label>
      ))}
    </div>
  );
}

// Create or edit a server connection to Meta, TikTok or GA4. `row` null = new. The token is never shown back.
export function ConnectionDialog({ row, meta, onClose, onSaved }) {
  const [platformKey, setPlatformKey] = useState(row?.platform || "meta");
  const [name, setName] = useState(row?.name || "");
  const [pixelId, setPixelId] = useState(row?.pixel_id || "");
  const [secret, setSecret] = useState("");
  const [testCode, setTestCode] = useState(row?.test_code || "");
  const [events, setEvents] = useState(row?.events || ["lead"]);
  const [names, setNames] = useState(row?.event_names || {});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const platform = meta.platforms[platformKey];
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const body = { name: name.trim(), pixel_id: pixelId.trim(), events, test_code: testCode.trim(),
      event_names: Object.fromEntries(events.map((k) => [k, (names[k] || "").trim()]).filter(([, v]) => v)) };
    if (secret.trim()) body.secret = secret.trim();
    try {
      const { data } = row
        ? await api.patch(`/postbacks/connections/${row.id}`, body)
        : await api.post("/postbacks/connections", { ...body, platform: platformKey });
      onSaved(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : (Array.isArray(d) && d[0]?.msg) || "Check the details and try again");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-none border-2 border-black sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="connection-dialog">
        <form onSubmit={save}>
          <DialogHeader>
            <DialogTitle className="font-chivo text-xl font-black">{row ? "Edit connection" : "New ad platform connection"}</DialogTitle>
            <DialogDescription>
              Our server tells {platform.label} when each moment below happens. No code is added to our pages.
              {meta.needs_approval ? " An admin approves it before it goes live." : ""}
            </DialogDescription>
          </DialogHeader>
          <Labeled id="connection-platform" label="Platform">
            <select id="connection-platform" value={platformKey} disabled={Boolean(row)} onChange={(e) => setPlatformKey(e.target.value)}
              className={`${INPUT} bg-white`} data-testid="connection-platform-select">
              {Object.entries(meta.platforms).map(([k, p]) => <option key={k} value={k}>{p.label}</option>)}
            </select>
          </Labeled>
          <Labeled id="connection-name" label="Name">
            <input id="connection-name" value={name} onChange={(e) => setName(e.target.value)} required maxLength={80}
              placeholder="e.g. Main Meta ad account" className={INPUT} data-testid="connection-name-input" />
          </Labeled>
          <Labeled id="connection-id" label={platform.id_label}>
            <input id="connection-id" value={pixelId} onChange={(e) => setPixelId(e.target.value)} required maxLength={40}
              className={`${INPUT} font-mono`} data-testid="connection-id-input" />
          </Labeled>
          <Labeled id="connection-secret" label={platform.secret_label}>
            <input id="connection-secret" type="password" autoComplete="off" value={secret} onChange={(e) => setSecret(e.target.value)}
              required={!row} placeholder={row ? `Leave blank to keep ${row.secret_hint || "the saved one"}` : ""}
              className={`${INPUT} font-mono`} data-testid="connection-secret-input" />
          </Labeled>
          <div className="text-[11px] text-zinc-600 mt-1">{HELP[platformKey]} We store it encrypted and never show it again.</div>
          {platformKey !== "ga4" && (
            <Labeled id="connection-test-code" label="Test Events code (for Send test)">
              <input id="connection-test-code" value={testCode} onChange={(e) => setTestCode(e.target.value)} maxLength={40}
                placeholder="e.g. TEST12345" className={`${INPUT} font-mono`} data-testid="connection-test-code-input" />
            </Labeled>
          )}
          <div className="label-mono mt-4">Send it when</div>
          <EventBoxes events={meta.events} value={events} onChange={setEvents} />
          <EventNames events={events} platform={platform} value={names} onChange={setNames} />
          {error && <div className="text-xs text-red-700 mt-3" data-testid="connection-error">{error}</div>}
          <DialogFooter className="gap-2 mt-5">
            <button type="button" onClick={onClose} className={BTN} data-testid="connection-cancel">Cancel</button>
            <button type="submit" disabled={busy || !events.length} className={BTN_PRIMARY} data-testid="connection-save">
              {busy ? "Saving…" : "Save connection"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
